/**
 * The sync engine.
 *
 * Push what this device made, pull what everyone else made, replay, repeat. The whole design
 * rests on events being append-only facts: they do not conflict, so there is no merge to get
 * wrong and no sale that can be lost to one.
 */

import { storedEnvelopeSchema, tryParseStoredEvent } from '@abastece/shared';
import {
  db,
  delMeta,
  getMeta,
  ordenReplay,
  pedirBorrarCopia,
  pendientes,
  setMeta,
  type LocalEvent,
} from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { aplicarNuevos } from '../db/aplicar.js';
import { ApiError, OfflineError, api } from './client.js';
import { traducir } from '../i18n/index.js';
import { enPractica } from '../lib/practica.js';
import { transferirFotos } from './fotos.js';

const PUSH_BATCH = 200;
const PULL_BATCH = 500;

export type SyncPhase =
  | 'inactivo'
  | 'sincronizando'
  | 'error'
  | 'sin-conexion'
  | 'sin-registrar'
  /** The server turned this device away. See `dadoDeBaja` in the device's meta. */
  | 'dado-de-baja'
  /** Practising. Nothing is sent, and nothing being sent is the correct state. */
  | 'practica';

export interface SyncState {
  fase: SyncPhase;
  pendientes: number;
  rechazados: number;
  ultimoError?: string;
  ultimaSync?: string;
  clockOffsetMs: number;
  /** True when the device clock is far enough off to corrupt event ordering and the corte de caja. */
  derivaAlta: boolean;
}

type Listener = (state: SyncState) => void;

let state: SyncState = {
  fase: 'inactivo',
  pendientes: 0,
  rechazados: 0,
  clockOffsetMs: 0,
  derivaAlta: false,
};

const listeners = new Set<Listener>();

export function suscribirSync(fn: Listener): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

function emitirEstado(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

export async function refrescarContadores(): Promise<void> {
  const [p, r] = await Promise.all([
    pendientes().count(),
    // What the person saw refused as they did it is not something to review (`rechazadoAqui`).
    db.eventos.where('estado').equals('rechazado').filter((e) => !e.rechazadoAqui).count(),
  ]);
  emitirEstado({ pendientes: p, rechazados: r });
}

/**
 * Threshold comes from settings, not from a constant here.
 *
 * Two minutes is a sane default, but a shop running four devices on a flaky network may want it
 * looser and one closing the caja to the minute may want it tighter. Hardcoding it would mean
 * the only way to change it is a deploy.
 */
async function registrarDeriva(derivaMs: number | undefined): Promise<void> {
  if (derivaMs === undefined) return;
  await setMeta('clockOffsetMs', derivaMs);
  const { relojDerivaMaxMs } = await readSettings();
  emitirEstado({ clockOffsetMs: derivaMs, derivaAlta: Math.abs(derivaMs) > relojDerivaMaxMs });
}

/**
 * What this device has waiting goes out in the order it was made (`deviceSeq`), a batch at a time.
 *
 * Not in the index's order: within `estado` that is the id's, and an id carries the phone's clock.
 * A clock set back with more than a batch waiting — fixed after the app warned of it, or corrected
 * by the network after days offline — sent later records ahead of earlier ones, and the server,
 * which applies each batch as it comes, refused a void or a corte that arrived before its sale.
 * Whatever is recorded while this runs goes in the sync queued behind it.
 */
async function empujar(): Promise<void> {
  const enSuOrden = (await pendientes().toArray()).sort(ordenReplay);
  let porLote = PUSH_BATCH;
  for (let i = 0; i < enSuOrden.length; ) {
    const lote = enSuOrden.slice(i, i + porLote);

    const payload = lote
      .map(({ estado, motivoRechazo, serverSeq, serverTs, ...env }) => {
        void estado;
        void motivoRechazo;
        void serverSeq;
        void serverTs;
        return env;
      });

    let res: Awaited<ReturnType<typeof api.push>>;
    try {
      res = await api.push(payload);
    } catch (e) {
      /*
       * More than the server reads at once (`PUSH_MAX_BYTES`, `app.ts`): the same records in halves,
       * in the same order. A single record that big — none of the shop's comes near — goes no
       * further, kept here as refused, so it does not hold back everything after it.
       */
      if (e instanceof ApiError && e.status === 413) {
        if (lote.length > 1) {
          porLote = Math.ceil(lote.length / 2);
          continue;
        }
        const motivo = traducir('sync.demasiadoGrande');
        await db.eventos.update(lote[0]!.id, { estado: 'rechazado', motivoRechazo: motivo });
        avisarRespuesta(lote[0]!.id, { estado: 'rechazado', motivo });
        i += 1;
        continue;
      }
      throw e;
    }
    i += lote.length;
    await registrarDeriva(res.relojDerivaMs);

    const rechazadosAhora = new Set(res.rechazados.flatMap((x) => (x.id ? [x.id] : [])));
    for (const ok of res.aceptados) if (!rechazadosAhora.has(ok.id)) avisarRespuesta(ok.id, { estado: 'confirmado' });

    await db.transaction('rw', db.eventos, async () => {
      for (const ok of res.aceptados) {
        const row = await db.eventos.get(ok.id);
        if (row) await db.eventos.put({ ...row, estado: 'confirmado', serverSeq: ok.serverSeq });
      }
      for (const bad of res.rechazados) {
        if (!bad.id) continue;
        anotarRechazo(bad.id, bad.reason);
        avisarRespuesta(bad.id, { estado: 'rechazado', motivo: bad.reason });
        const row = await db.eventos.get(bad.id);
        /**
         * A rejected event is kept, not deleted. It stops being retried and starts being
         * visible: something a person can look at and decide about. Silently dropping it
         * would lose a sale with no trace that it ever happened.
         */
        if (row) await db.eventos.put({ ...row, estado: 'rechazado', motivoRechazo: bad.reason });
      }
    });
  }
}

async function jalar(): Promise<void> {
  let desde = (await getMeta('ultimoServerSeq')) ?? 0;

  for (;;) {
    const res = await api.pull(desde, PULL_BATCH);
    await registrarDeriva(res.relojDerivaMs);
    if (res.eventos.length === 0) break;

    await db.transaction('rw', db.eventos, async () => {
      for (const raw of res.eventos) {
        const copia = copiaDelServidor(raw);
        if (!copia) continue;
        /**
         * This is also how a device's own events come back to it. Overwriting the local row
         * with the server's copy is what stamps the real serverSeq onto an event that was
         * created offline, which is what lets the next replay order it honestly against
         * everyone else's.
         */
        await db.eventos.put(copia);
      }
    });

    desde = res.serverSeq;
    await setMeta('ultimoServerSeq', desde);
    if (!res.hayMas) break;
  }
}

/**
 * Once: what this device pulled before it kept the server's whole record is pulled again, whole.
 *
 * Until then a pulled event was kept as this code read it, and a field it did not know yet was
 * left out of the copy for good — a purchase's IVA, the price list on a sale's line, how a worker
 * is paid, an order's delivery charge — and an event of a kind it did not know was not kept at
 * all. The replay a new version asks for (`VERSION_PROYECCIONES`) works from that copy, so it
 * could not bring them back. Only the copies already here are put back, in place: nothing new is
 * applied and the high-water mark does not move, so a pull cut off halfway changes nothing but
 * where the next one goes on from. Then the whole log is replayed from the copies.
 */
async function recopiar(): Promise<void> {
  if (await getMeta('copiaCompleta')) return;
  const hasta = (await getMeta('ultimoServerSeq')) ?? 0;
  let desde = (await getMeta('recopiadoHasta')) ?? 0;

  while (desde < hasta) {
    const res = await api.pull(desde, PULL_BATCH);
    if (res.eventos.length === 0) break;
    await db.transaction('rw', db.eventos, async () => {
      for (const raw of res.eventos) {
        const copia = copiaDelServidor(raw);
        if (copia && copia.serverSeq !== undefined && copia.serverSeq <= hasta) await db.eventos.put(copia);
      }
    });
    desde = res.serverSeq;
    await setMeta('recopiadoHasta', desde);
    if (!res.hayMas) break;
  }

  await db.transaction('rw', db.meta, async () => {
    await setMeta('copiaCompleta', true);
    await delMeta('recopiadoHasta');
    // From the whole copies: the next `aplicarNuevos` replays everything.
    await delMeta('aplicadoHasta');
  });
}

export interface SyncResult {
  ok: boolean;
  motivo?: string;
}

/**
 * Coalesced: at most one sync in flight, and at most one more queued behind it.
 *
 * This used to be `if (corriendo) return { ok: true }` — a success that had done nothing. The
 * damage was not the wasted call but the lie: `accesoDeDesarrollo` awaits a sync and then asks
 * whether the shop has any products, and a sync that returns instantly meant the answer was
 * "none" for a shop that had twenty-four. It seeded a second time on top of itself, which is
 * how an installation ended up with three price lists called Público, all three flagged as the
 * default.
 *
 * So a caller now always gets a sync that finished AFTER it asked. Latecomers share one
 * trailing run rather than each queueing their own: whatever they emitted is already in the
 * outbox, so a single extra pass carries all of it.
 */
let enVuelo: Promise<SyncResult> | null = null;
let encolada: Promise<SyncResult> | null = null;

export function sincronizar(): Promise<SyncResult> {
  if (!enVuelo) {
    enVuelo = correrSync().finally(() => {
      enVuelo = null;
    });
    return enVuelo;
  }
  encolada ??= enVuelo.catch(() => undefined).then(() => {
    encolada = null;
    return sincronizar();
  });
  return encolada;
}

/**
 * What the server turned away in this page's pushes, by event id, and why: kept here because the
 * row does not keep it. The server holds a refused event in its log all the same, so the next pull
 * brings it back as `confirmado`, and only the push's answer said it was refused. The last few
 * hundred are plenty: it is read right after the push that answered.
 */
const rechazadosPorElServidor = new Map<string, string>();

function anotarRechazo(id: string, motivo: string): void {
  rechazadosPorElServidor.set(id, motivo);
  if (rechazadosPorElServidor.size > 300) rechazadosPorElServidor.delete(rechazadosPorElServidor.keys().next().value!);
}

/** Who is waiting for the push's answer on a record (`esperarRespuesta`), by its id. */
const esperandoRespuesta = new Map<string, (c: Confirmacion) => void>();

function avisarRespuesta(id: string, c: Confirmacion): void {
  esperandoRespuesta.get(id)?.(c);
}

/**
 * The push's answer on a record this device just wrote, and nothing more: no pull, no replay. For the
 * till, which waits on it before the receipt (`venderSoloLoQueHay`, 2026-10-08): a round trip and the
 * server's work, a few hundred milliseconds. No answer within `ms`, or no connection, is
 * `sin-respuesta`, and the record stays in the outbox like any other.
 */
export async function esperarRespuesta(eventoId: string, ms = 5_000): Promise<Confirmacion> {
  let listo: (c: Confirmacion) => void = () => {};
  const respuesta = new Promise<Confirmacion>((r) => {
    listo = r;
  });
  const reloj = setTimeout(() => listo({ estado: 'sin-respuesta' }), ms);
  esperandoRespuesta.set(eventoId, listo);
  try {
    // Answered already, by a push that went out before this was asked.
    const motivo = rechazadosPorElServidor.get(eventoId);
    if (motivo !== undefined) return { estado: 'rechazado', motivo };
    if ((await db.eventos.get(eventoId))?.serverSeq !== undefined) return { estado: 'confirmado' };
    void sincronizar();
    return await respuesta;
  } finally {
    clearTimeout(reloj);
    esperandoRespuesta.delete(eventoId);
  }
}

/** A sync, waited for up to `ms`: still going by then counts as no answer. */
async function sincronizarHasta(ms: number): Promise<SyncResult | null> {
  let reloj: ReturnType<typeof setTimeout> | undefined;
  const r = await Promise.race([
    sincronizar(),
    new Promise<null>((listo) => {
      reloj = setTimeout(() => listo(null), ms);
    }),
  ]);
  clearTimeout(reloj);
  return r;
}

/**
 * Up to date with the server, within `ms`: what every other phone has sent is here and applied,
 * and what this one had waiting has gone. False with no connection, or no answer in time.
 */
export async function alDiaConElServidor(ms = 15_000): Promise<boolean> {
  return (await sincronizarHasta(ms))?.ok === true;
}

/** What the server said of one record: took it into its log, turned it away and why, or nothing yet. */
export type Confirmacion = { estado: 'confirmado' } | { estado: 'rechazado'; motivo: string } | { estado: 'sin-respuesta' };

/**
 * Waits for the server's word on a record this device just wrote: a sync, and then what the push
 * answered. For what is only taken with a connection, an order setting stock aside (the owner,
 * 2026-10-08): the server puts every phone's records in one order, so its word is the one that
 * holds on every device. No answer within `ms`, or no connection, is `sin-respuesta`: the record
 * waits to be sent like any other, and the caller decides what that means.
 *
 * `confirmado` is the server's log, and no refusal heard. A push whose answer was lost and was
 * sent again only hears «already here», so whoever must be sure also looks at what the record
 * made once the sync has applied the server's order: an order refused there is not in the books.
 */
export async function confirmarConElServidor(eventoId: string, ms = 15_000): Promise<Confirmacion> {
  await sincronizarHasta(ms);
  const motivo = rechazadosPorElServidor.get(eventoId);
  if (motivo !== undefined) return { estado: 'rechazado', motivo };
  const e = await db.eventos.get(eventoId);
  if (e?.estado === 'rechazado') return { estado: 'rechazado', motivo: e.motivoRechazo ?? '' };
  if (e?.serverSeq !== undefined) return { estado: 'confirmado' };
  return { estado: 'sin-respuesta' };
}

async function correrSync(): Promise<SyncResult> {
  /**
   * A practice shop never talks to the server.
   *
   * Not an optimisation — a containment. A practice sale that reached the log would come back
   * down to every real device in the shop indistinguishable from a real one, and there is no
   * way to un-say it. The status line reports it as its own state rather than as an error,
   * because nothing is wrong.
   */
  if (enPractica()) {
    emitirEstado({ fase: 'practica' });
    return { ok: true };
  }

  const token = await getMeta('token');
  if (!token) {
    emitirEstado({ fase: 'sin-registrar' });
    return { ok: false, motivo: traducir('error.sinRegistrar') };
  }

  emitirEstado({ fase: 'sincronizando' });

  try {
    await empujar();
    await recopiar();
    await jalar();

    // What arrived goes into its place in the server's order, ahead of whatever this device had
    // applied too early (see `db/aplicar.ts`).
    await aplicarNuevos();

    await setMeta('ultimaSyncTs', new Date().toISOString());
    // Taken back after all (see `DadoDeBaja`, «Intentar otra vez»): the app opens again.
    if (await getMeta('dadoDeBaja')) await delMeta('dadoDeBaja');
    await refrescarContadores();
    emitirEstado({ fase: 'inactivo', ultimaSync: new Date().toISOString(), ultimoError: undefined });

    // Photos move after the records, and nobody waits for them: see `sync/fotos.ts`.
    void transferirFotos();
    return { ok: true };
  } catch (e) {
    if (e instanceof OfflineError) {
      // Expected, not exceptional. The device keeps working; it simply has nowhere to send yet.
      emitirEstado({ fase: 'sin-conexion' });
      return { ok: false, motivo: 'Sin conexión' };
    }
    /**
     * A 401 means the server does not recognise this device any more.
     *
     * In a shop that is a revoked phone and the right answer is to stop and say so. On a
     * development server it usually means the database was reset underneath a browser that
     * still holds the old token — and leaving that device permanently unable to sync, with no
     * way back through an install screen that refuses an installed system, is exactly the dead
     * end auto-enrolment exists to remove. So it re-enrols itself and reloads.
     */
    if (e instanceof ApiError && e.status === 401 && (await reenrolarSiEsDesarrollo())) {
      return { ok: false, motivo: 'Dispositivo re-registrado' };
    }
    /**
     * Anywhere else a 401, like the 403 a revoked device gets, means the server will not take
     * anything from this device again: the owner revoked it, or the server no longer knows its
     * token. What it records from here would never reach the shop, so the app stops (see
     * `DadoDeBaja`) rather than take sales nobody will ever see.
     */
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      await setMeta('dadoDeBaja', true);
      emitirEstado({ fase: 'dado-de-baja', ultimoError: e.message });
      return { ok: false, motivo: e.message };
    }
    const motivo = e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
    emitirEstado({ fase: 'error', ultimoError: motivo });
    return { ok: false, motivo };
  } finally {
    await refrescarContadores();
  }
}

/**
 * Only on a development server, and only once — the flag stops a loop if whatever caused the
 * 401 is still there after a reload.
 *
 * It drops this device's whole copy, not just the credential. A development server that no
 * longer knows the device has almost always had its database reset, and the copy then
 * describes a shop that exists nowhere else. Dropping only the credential hid the reset: the
 * old products stayed on screen, the pull brought nothing, and the demo never reseeded because
 * the shop did not look empty. With nothing kept, the reload boots like a fresh install.
 *
 * It reloads rather than re-enrolling inline: this runs inside a sync that is still marked as
 * running, so calling back into one would return immediately and the seeding that follows would
 * see a shop it had not pulled yet. A fresh boot does the whole thing in order, which is what
 * `arrancar` already knows how to do.
 */
let yaReenrolado = false;

async function reenrolarSiEsDesarrollo(): Promise<boolean> {
  if (yaReenrolado) return false;
  try {
    if (!(await api.health()).instalacionAbierta) return false;
  } catch {
    return false;
  }
  yaReenrolado = true;
  pedirBorrarCopia();
  location.reload();
  return true;
}

const INTERVALO_MS = 30_000;

/** Periodic sync, plus an immediate attempt whenever the browser says it is back online. */
export function iniciarSyncAutomatica(): () => void {
  const tick = () => void sincronizar();
  const timer = setInterval(tick, INTERVALO_MS);
  const alVolver = () => void sincronizar();

  window.addEventListener('online', alVolver);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });

  void sincronizar();

  return () => {
    clearInterval(timer);
    window.removeEventListener('online', alVolver);
  };
}

/**
 * What a pulled event is kept as: the server's record, whole, with what this code reads in it put
 * over it (the defaults its schema fills in).
 *
 * The server's record and not this code's reading of it: a phone runs the code it last loaded,
 * and keeps pulling while a newer one already writes fields — and kinds of event — it does not
 * know. Read, they would be left out; kept, they wait for the version that knows them, which
 * replays the log from these copies. An event this code cannot read at all is kept as it came:
 * the replay gives it its place and changes nothing with it (`db/aplicar.ts`), until a version
 * that reads it. Only a record that is not an event at all, which the server never sends, is
 * dropped.
 */
export function copiaDelServidor(raw: unknown): LocalEvent | null {
  const sobre = storedEnvelopeSchema.safeParse(raw);
  if (!sobre.success) {
    console.warn('[sync] registro inválido recibido del servidor, se omite', raw);
    return null;
  }
  const crudo = (raw as { payload?: unknown }).payload;
  const leido = tryParseStoredEvent(raw);
  const payload = leido.ok ? conservando(crudo, leido.event.payload) : crudo;
  return { ...sobre.data, payload, estado: 'confirmado' };
}

/** `leido` over `crudo`, key by key and item by item: what only `crudo` has stays. */
function conservando(crudo: unknown, leido: unknown): unknown {
  if (Array.isArray(crudo) && Array.isArray(leido)) {
    return crudo.length === leido.length ? leido.map((x, i) => conservando(crudo[i], x)) : leido;
  }
  if (esObjeto(crudo) && esObjeto(leido)) {
    const junto: Record<string, unknown> = { ...crudo };
    for (const [clave, valor] of Object.entries(leido)) junto[clave] = conservando(crudo[clave], valor);
    return junto;
  }
  return leido;
}

function esObjeto(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}
